import React from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import './PendingCards.css';
import { formatDate } from '../../utils/helper';
import { PENDING_CARDS_RANGE_TIME, INSPECTION_STATUS } from '../../utils/constants';

const { PENDING_USER } = INSPECTION_STATUS;

const PendingCards = ({ 
    inspections = [], 
    onCardClick,
    showImages = true,
    title = "Inspecciones Pendientes por Inspección o con Fotos Faltantes",
    navigateToDetail,
    navigateToVerdict
}) => {
    // Configuración responsive para el carrusel
    const responsive = {
        superLargeDesktop: {
            breakpoint: { max: 4000, min: 1536 },
            items: 4,
            slidesToSlide: 1
        },
        desktop: {
            breakpoint: { max: 1536, min: 1024 },
            items: 3,
            slidesToSlide: 1
        },
        tablet: {
            breakpoint: { max: 1024, min: 768 },
            items: 2,
            slidesToSlide: 1
        },
        mobile: {
            breakpoint: { max: 768, min: 0 },
            items: 1,
            slidesToSlide: 1
        }
    };

    const handleCardClick = (inspection) => {
        if(inspection.id.includes('INS')) {
            navigateToDetail(inspection.id);
        } else {
            navigateToVerdict(inspection.id);
        }
    };

    // Función para clasificar los items pendientes en dos categorías
    const classifyPendingItems = (pendingItems) => {
        const userPendingItems = [];
        const inspectionPendingItems = [];

        if (!pendingItems || pendingItems.length === 0) {
            return { userPendingItems: [], inspectionPendingItems: [] };
        }

        pendingItems.forEach(item => {
            if (item === 'Fotos del vehículo') {
                userPendingItems.push(item);
            } else {
                // Dictamen técnico, Antecedentes del vehículo, etc.
                inspectionPendingItems.push(item);
            }
        });

        return { userPendingItems, inspectionPendingItems };
    };

    if (inspections.length === 0) {
        return (
            <div className="pending-cards-container">
                <h2 className="pending-cards-title">{title}</h2>
                <div className="no-pending-inspections">
                    <p>No hay inspecciones pendientes por inspección o con fotos faltantes</p>
                </div>
            </div>
        );
    }
    
    const getCardBackgroundColor = (readyForReviewAt) => {
        if (!readyForReviewAt) {
            return 'normal';
        }

        const now = new Date();
        const reviewDate = new Date(readyForReviewAt);
        const timeDiffInMinutes = (now - reviewDate) / (1000 * 60);

        // Rangos en minutos:
        // Normal: menos de 5 minutos
        // Warning: entre 5 y 15 minutos
        // Critical: más de 15 minutos
        if (timeDiffInMinutes < 5) {
            return 'normal';
        } else if (timeDiffInMinutes <= 15) {
            return 'warning';
        } else {
            return 'critical';
        }
    };

    const getCardColorClass = (readyforReviewAt) => {
        const colorType = getCardBackgroundColor(readyforReviewAt);
        return `pending-card-${colorType}`;
    };

    return (
        <div className="pending-cards-container">
            <h2 className="pending-cards-title">{title}</h2>
            
            <div className="carousel-wrapper">
                <Carousel
                    responsive={responsive}
                    swipeable={true}
                    draggable={true}
                    showDots={true}
                    infinite={false}
                    autoPlay={false}
                    keyBoardControl={true}
                    customTransition="transform 300ms ease-in-out"
                    transitionDuration={300}
                    containerClass="pending-cards-carousel"
                    removeArrowOnDeviceType={["tablet", "mobile"]}
                    dotListClass="custom-dot-list-style"
                    itemClass="carousel-item-padding-40-px"
                    arrows={true}
                    renderButtonGroupOutside={false}
                    renderDotsOutside={true}
                    partialVisible={false}
                    centerMode={false}
                >
                    {inspections.map((inspection, index) => {
                        const { userPendingItems, inspectionPendingItems } = classifyPendingItems(inspection.pendingItems);
                        const hasUserPending = userPendingItems.length > 0;
                        const hasInspectionPending = inspectionPendingItems.length > 0;

                        return (
                            <div 
                                key={inspection.id || index} 
                                className={`pending-card card-${(index % 3) + 1} ${onCardClick ? 'clickable' : ''} ${getCardColorClass(inspection.readyForReviewAt)}`}
                                onClick={() => handleCardClick(inspection)}
                            >
                                <div className="card-header">
                                    <div className="card-id">PLACA: {inspection.licensePlate}</div>
                                    <div className="card-details">
                                        <div>Fecha de Creación: {formatDate(inspection.date)}</div>
                                        {inspection.readyForReviewAt && <div>Listo para Revisión: {formatDate(inspection.readyForReviewAt)}</div>}
                                        <div>Empresa: {inspection.company}</div>
                                    </div>
                                </div>
                                
                                {showImages && inspection.image && (
                                    <div className="card-image">
                                        <img src={inspection.image} alt="Vehículo" />
                                    </div>
                                )}
                                
                                <div className="card-pending">
                                    {/* Pendientes por Usuario */}
                                    {hasUserPending && (
                                        <>
                                            <h4>Pendientes por Usuario:</h4>
                                            <ul>
                                                {userPendingItems.map((item, itemIndex) => (
                                                    <li key={`user-${itemIndex}`}>{item}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}

                                    {/* Pendientes por Inspección */}
                                    {hasInspectionPending && (
                                        <>
                                            {hasUserPending && <div className="pending-section-divider"></div>}
                                            <h4>Pendientes por Inspección:</h4>
                                            <ul>
                                                {inspectionPendingItems.map((item, itemIndex) => (
                                                    <li key={`inspection-${itemIndex}`}>{item}</li>
                                                ))}
                                            </ul>
                                        </>
                                    )}

                                    {/* Si no hay ningún pendiente (caso edge) */}
                                    {!hasUserPending && !hasInspectionPending && (
                                        <p className="no-pending-items">No hay elementos pendientes</p>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </Carousel>
            </div>
        </div>
    );
};

export default PendingCards;